import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/ui/page-header';
import AdminJsonToggle from './AdminJsonToggle';
import type { EnhancedModule } from '@/types/api/enhanced-module';

interface DynamicModuleScreenProps {
  module: EnhancedModule;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  onExit?: () => void;
}

/**
 * DynamicModuleScreen renders module content based on backend configuration.
 * This component replaces hardcoded introduction screens with data-driven UI.
 */
const DynamicModuleScreen: React.FC<DynamicModuleScreenProps> = ({
  module,
  onPrimaryAction,
  onSecondaryAction,
  onExit,
}) => {
  const navigate = useNavigate();
  const [editableModule, setEditableModule] = useState(module);

  // Default handlers if not provided
  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    } else if (editableModule.completionRoute) {
      navigate(editableModule.completionRoute);
    }
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    }
  };

  const handleExit = () => {
    if (onExit) {
      onExit();
    } else {
      navigate(editableModule.exitRoute || '/updates');
    }
  };

  // Show loading state if no module data
  if (!editableModule) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Determine page header title based on screen type
  const getHeaderTitle = () => {
    switch (editableModule.screenType) {
      case 'survey':
      case 'survey_intro':
        return 'Survey';
      case 'profile':
        return 'Profile';
      case 'coaching':
        return 'Coaching';
      case 'info':
        return 'Information';
      default:
        return editableModule.name;
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <PageHeader 
        title={getHeaderTitle()}
        variant="title-with-exit"
        onExit={handleExit}
      />
      
      <AdminJsonToggle 
        data={editableModule}
        onUpdate={setEditableModule}
        label="Module Configuration"
      />
      
      <div className="flex-1 flex flex-col justify-start px-6 pt-8">
        
        {/* Main Content Section */}
        <div className="mb-8">
          {/* Title */}
          {editableModule.uiTitle && (
            <h2 className="text-4xl font-semibold text-black mb-6 leading-tight">
              {editableModule.uiTitle}
            </h2>
          )}
          
          {/* Subtitle */}
          {editableModule.uiSubtitle && (
            <h3 className="text-2xl text-gray-600 mb-4 whitespace-pre-wrap">
              {editableModule.uiSubtitle}
            </h3>
          )}
          
          {/* Description */}
          {editableModule.uiDescription && (
            <p className="text-lg text-[#666666] mb-6 leading-relaxed whitespace-pre-wrap">
              {editableModule.uiDescription}
            </p>
          )}
          
          {/* Instructions */}
          {editableModule.uiInstructions && (
            <p className="text-lg text-[#666666] mb-8 leading-relaxed whitespace-pre-wrap">
              {editableModule.uiInstructions}
            </p>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="space-y-4">
          {/* Primary Button */}
          {editableModule.primaryButtonText && (
            <Button
              onClick={handlePrimaryAction}
              className="w-full bg-black text-white hover:bg-gray-800 rounded-full py-6 text-lg min-touch-target"
            >
              {editableModule.primaryButtonText}
            </Button>
          )}
          
          {/* Secondary Button */}
          {editableModule.secondaryButtonText && (
            <Button
              onClick={handleSecondaryAction}
              variant="outline"
              className="w-full border-gray-300 text-black hover:bg-gray-50 rounded-full py-6 text-lg min-touch-target"
            >
              {editableModule.secondaryButtonText}
            </Button>
          )}
        </div>
        
        {/* Time Estimate */}
        {editableModule.estimatedMinutes && (
          <p className="text-sm text-[#666666] mt-6 text-center">
            Takes about {editableModule.estimatedMinutes} min to complete
          </p>
        )}
      </div>
    </div>
  );
};

export default DynamicModuleScreen;